import { Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';
import { AudienceFilter, AudiencePreview } from '../../domain/models/audience-filter.model';
import { Campaign } from '../../domain/models/campaign.model';
import { CampaignInput } from '../../domain/models/campaign-input.model';
import { CampaignKpi } from '../../domain/models/campaign-kpi.model';
import { CampaignListQuery } from '../../domain/models/campaign-list-query.model';
import { CampaignListResult } from '../../domain/models/campaign-list-result.model';
import { CampaignRecipient } from '../../domain/models/campaign-recipient.model';
import { EmailPreviewRequest } from '../../domain/models/email-preview-request.model';
import { ICampaignsRepository } from '../../domain/repositories/campaigns.repository';
import { CampaignsRemoteDatasource } from '../datasources/remote/campaigns.remote-datasource';
import { CampaignMapper } from '../mappers/campaign.mapper';

@Injectable()
export class CampaignsRepositoryImpl implements ICampaignsRepository {
  private readonly datasource = inject(CampaignsRemoteDatasource);
  private readonly mapper = inject(CampaignMapper);

  public list(organizationId: number, query: CampaignListQuery): Observable<CampaignListResult> {
    return this.datasource.list(organizationId, query.page, query.pageSize, query.status, query.searchTerm).pipe(
      map((dto) => ({
        items: dto.Items.map((item) => this.mapper.toCampaign(item)),
        total: dto.TotalCount,
      })),
    );
  }

  public getKpis(organizationId: number): Observable<CampaignKpi> {
    return this.datasource.getKpis(organizationId).pipe(map((dto) => this.mapper.toKpi(dto)));
  }

  public getById(organizationId: number, campaignId: string): Observable<Campaign> {
    return this.datasource.getById(organizationId, campaignId).pipe(map((dto) => this.mapper.toCampaign(dto)));
  }

  public getRecipients(organizationId: number, campaignId: string): Observable<readonly CampaignRecipient[]> {
    return this.datasource.getRecipients(organizationId, campaignId).pipe(
      map((dtos) => dtos.map((dto) => this.mapper.toRecipient(dto))),
    );
  }

  public previewAudience(organizationId: number, filter: AudienceFilter): Observable<AudiencePreview> {
    return this.datasource.previewAudience(organizationId, this.mapper.toAudienceFilterRequest(filter)).pipe(
      map((dto) => this.mapper.toAudiencePreview(dto)),
    );
  }

  public previewEmail(organizationId: number, request: EmailPreviewRequest): Observable<string> {
    return this.datasource.previewEmail(organizationId, this.mapper.toEmailPreviewRequest(request));
  }

  public create(organizationId: number, input: CampaignInput): Observable<Campaign> {
    return this.datasource.create(organizationId, this.mapper.toCreateRequest(input)).pipe(
      map((dto) => this.mapper.toCampaign(dto)),
    );
  }

  public update(organizationId: number, campaignId: string, input: CampaignInput): Observable<Campaign> {
    return this.datasource.update(organizationId, campaignId, this.mapper.toCreateRequest(input)).pipe(
      map((dto) => this.mapper.toCampaign(dto)),
    );
  }

  public delete(organizationId: number, campaignId: string): Observable<void> {
    return this.datasource.delete(organizationId, campaignId);
  }

  public duplicate(organizationId: number, campaignId: string): Observable<Campaign> {
    return this.datasource.duplicate(organizationId, campaignId).pipe(map((dto) => this.mapper.toCampaign(dto)));
  }

  public pause(organizationId: number, campaignId: string): Observable<Campaign> {
    return this.datasource.pause(organizationId, campaignId).pipe(map((dto) => this.mapper.toCampaign(dto)));
  }

  public schedule(organizationId: number, campaignId: string, scheduledAtUtc: string): Observable<Campaign> {
    return this.datasource.schedule(organizationId, campaignId, { ScheduledAtUtc: scheduledAtUtc }).pipe(
      map((dto) => this.mapper.toCampaign(dto)),
    );
  }

  public sendNow(organizationId: number, campaignId: string): Observable<Campaign> {
    return this.datasource.sendNow(organizationId, campaignId).pipe(map((dto) => this.mapper.toCampaign(dto)));
  }
}
